import React, { createContext, useContext, useEffect, useState } from 'react';
import { SettingsService } from '../services/settingsService';
import { CompanySettings } from '../types';

interface BrandingContextType {
  settings: CompanySettings | null;
  loading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
  applyBranding: (settings: CompanySettings) => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyBranding = (brandingSettings: CompanySettings) => {
    const root = document.documentElement;

    // Header/Navigation
    root.style.setProperty('--header-background', brandingSettings.colors.headerBackground);
    root.style.setProperty('--header-text', brandingSettings.colors.headerText);
    root.style.setProperty('--header-button', brandingSettings.colors.headerButton);

    // Main Content
    root.style.setProperty('--main-background', brandingSettings.colors.mainBackground);
    root.style.setProperty('--main-text', brandingSettings.colors.mainText);
    root.style.setProperty('--secondary-text', brandingSettings.colors.secondaryText);

    // Cards/Windows
    root.style.setProperty('--card-background', brandingSettings.colors.cardBackground);
    root.style.setProperty('--card-text', brandingSettings.colors.cardText);

    // Buttons
    root.style.setProperty('--primary-button', brandingSettings.colors.primaryButton);
    root.style.setProperty('--primary-button-text', brandingSettings.colors.primaryButtonText);
    root.style.setProperty('--secondary-button', brandingSettings.colors.secondaryButton);
    root.style.setProperty('--secondary-button-text', brandingSettings.colors.secondaryButtonText);

    // UI Elements
    root.style.setProperty('--border-color', brandingSettings.colors.border);
    root.style.setProperty('--accent-color', brandingSettings.colors.accent);

    // Status Colors
    root.style.setProperty('--success-color', brandingSettings.colors.success);
    root.style.setProperty('--warning-color', brandingSettings.colors.warning);
    root.style.setProperty('--error-color', brandingSettings.colors.error);

    // Typography
    root.style.setProperty('--font-family', brandingSettings.typography.fontFamily);
    root.style.setProperty('--font-size-h1', `${brandingSettings.typography.fontScale.h1}px`);
    root.style.setProperty('--font-size-h2', `${brandingSettings.typography.fontScale.h2}px`);
    root.style.setProperty('--font-size-h3', `${brandingSettings.typography.fontScale.h3}px`);
    root.style.setProperty('--font-size-body', `${brandingSettings.typography.fontScale.body}px`);

    // Update page title
    if (brandingSettings.branding.companyName) {
      document.title = brandingSettings.branding.companyName + ' - Quoting System';
    }

    // Update favicon with cache busting
    if (brandingSettings.branding.faviconUrl) {
      // Remove all existing favicon links to prevent conflicts
      const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      existingFavicons.forEach((link) => link.remove());

      // Create new favicon link with cache busting
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = 'image/x-icon';

      // Add timestamp to prevent browser caching issues
      const faviconUrl = brandingSettings.branding.faviconUrl;
      const cacheBuster = faviconUrl.includes('?') ? '&' : '?';
      faviconLink.href = `${faviconUrl}${cacheBuster}v=${Date.now()}`;

      document.head.appendChild(faviconLink);

      console.log('Favicon updated:', faviconLink.href);
    }

    // Cache branding in localStorage for faster initial load
    try {
      localStorage.setItem('cachedBranding', JSON.stringify(brandingSettings));
    } catch (e) {
      // localStorage might be unavailable, that's ok
    }

    console.log('Branding applied successfully');
  };

  const refreshBranding = async () => {
    try {
      setLoading(true);
      setError(null);
      const brandingSettings = await SettingsService.getSettings();
      setSettings(brandingSettings);
      applyBranding(brandingSettings);
    } catch (err: any) {
      setError(err.message);
      console.error('Failed to load branding settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // First, try to apply cached branding immediately to prevent flash
    try {
      const cached = localStorage.getItem('cachedBranding');
      if (cached) {
        const brandingSettings = JSON.parse(cached) as CompanySettings;
        applyBranding(brandingSettings);
        console.log('Applied cached branding immediately');
      }
    } catch (e) {
      // localStorage might be unavailable, that's ok
    }

    // Then load fresh branding from Firestore
    refreshBranding();

    // Set up periodic refresh every 30 seconds to catch any updates
    const interval = setInterval(refreshBranding, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <BrandingContext.Provider value={{ settings, loading, error, refreshBranding, applyBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBrandingContext = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBrandingContext must be used within a BrandingProvider');
  }
  return context;
};
